<template>
  <mounting-portal append mount-to="#dialog-root">
    <transition name="overlay">
      <div v-if="loading" class="loading">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          preserveAspectRatio="xMidYMid"
          viewBox="0 0 100 100"
        >
          <circle
            cx="50"
            cy="50"
            r="35"
            fill="none"
            stroke="currentColor"
            stroke-dasharray="164.9 57"
            stroke-width="6"
            transform="rotate(275.8 50 50)"
          >
            <animateTransform
              attributeName="transform"
              begin="0s"
              calcMode="linear"
              dur="1s"
              keyTimes="0;1"
              repeatCount="indefinite"
              type="rotate"
              values="0 50 50;360 50 50"
            />
          </circle>
        </svg>
      </div>
    </transition>
    <transition name="modal" @after-enter="onAfterEnter">
      <form
        v-if="isOpen"
        ref="$form"
        :id="dataModalId"
        class="BluFutureForm"
        method="post"
        action="#"
        :style="{ '--offset-top': `${cmsMenuOffset}px` }"
        @submit.prevent="onSubmit"
      >
        <button
          type="button"
          @click.prevent="closeModal"
          class="close"
          :title="config.close"
        >
          <icon name="times" />
        </button>
        <container class="fields-container">
          <div class="main-row row justify-content-center align-items-center">
            <div class="intro-col col-md-10 col-lg-8">
              <template v-if="confirm">
                <h3 class="title-40 text-color-blue">
                  {{ config.confirmTitle }}
                </h3>
                <p
                  class="text-20 text-light pt-3 pt-md-4"
                  v-html="config.confirmText"
                />
                <div class="pt-4 pt-md-5">
                  <btn
                    tag="button"
                    type="button"
                    class="blue-lochmara"
                    @click.prevent="closeModal"
                  >
                    {{ config.close }}
                  </btn>
                </div>
              </template>
              <template v-else>
                <h3 class="title-40 text-color-blue">
                  {{ config.introTitle }}
                </h3>
                <p
                  class="text-20 text-light pt-3 pt-md-4"
                  v-html="config.introText"
                />
              </template>
            </div>
            <div v-if="!confirm" class="fields-col col-md-10 col-lg-8">
              <div class="fields-row row">
                <div class="col-12">
                  <field-input
                    id="form-name"
                    name="name"
                    :label="config.name.label"
                    :placeholder="config.name.placeholder"
                    :field="$v.name"
                    :messages="{
                      required: config.name.requiredErrorMessage,
                      serverError: getServerErrorMessage('name')
                    }"
                    :show-error="$v.$dirty"
                    :maxlength="200"
                  />
                </div>
                <div class="col-lg-6">
                  <field-input
                    id="form-email"
                    name="email"
                    :label="config.email.label"
                    :placeholder="config.email.placeholder"
                    :field="$v.email"
                    :messages="{
                      required: config.email.requiredErrorMessage,
                      email: config.email.formatErrorMessage,
                      serverError: getServerErrorMessage('email')
                    }"
                    :show-error="$v.$dirty"
                    :maxlength="70"
                  />
                </div>
                <div class="col-lg-6">
                  <field-label for-id="form-phone-prefix">{{
                    config.phone.label
                  }}</field-label>
                  <div class="row">
                    <div class="col-4">
                      <field-select
                        id="form-phone-prefix"
                        name="phone_prefix"
                        :field="$v.phone_prefix"
                        :messages="{
                          required: config.phone_prefix.requiredErrorMessage,
                          serverError: getServerErrorMessage('phone_prefix')
                        }"
                        :show-error="$v.$dirty"
                        :options="phonePrefixes"
                      />
                    </div>
                    <div class="col-8">
                      <field-input
                        id="form-phone"
                        name="phone"
                        :placeholder="config.phone.placeholder"
                        :field="$v.phone"
                        :messages="{
                          required: config.phone.requiredErrorMessage,
                          phone: config.phone.formatErrorMessage,
                          serverError: getServerErrorMessage('phone')
                        }"
                        :show-error="$v.$dirty"
                        :maxlength="16"
                      />
                    </div>
                  </div>
                </div>
                <div class="col-sm-6 col-md-7 col-lg-8">
                  <field-input
                    id="form-founder"
                    name="founder"
                    :label="config.founder.label"
                    :placeholder="config.founder.placeholder"
                    :field="$v.founder"
                    :messages="{
                      required: config.founder.requiredErrorMessage,
                      serverError: getServerErrorMessage('founder')
                    }"
                    :show-error="$v.$dirty"
                    :maxlength="150"
                  />
                </div>
                <div class="col-sm-6 col-md-5 col-lg-4">
                  <field-input
                    id="form-employees"
                    name="employees"
                    :label="config.employees.label"
                    :placeholder="config.employees.placeholder"
                    :field="$v.employees"
                    :messages="{
                      required: config.employees.requiredErrorMessage,
                      serverError: getServerErrorMessage('employees')
                    }"
                    :show-error="$v.$dirty"
                    :maxlength="20"
                  />
                </div>
                <div class="col-md-6">
                  <field-input
                    id="form-headquarter"
                    name="headquarter"
                    :label="config.headquarter.label"
                    :placeholder="config.headquarter.placeholder"
                    :field="$v.headquarter"
                    :messages="{
                      required: config.headquarter.requiredErrorMessage,
                      serverError: getServerErrorMessage('headquarter')
                    }"
                    :show-error="$v.$dirty"
                    :maxlength="400"
                  />
                </div>
                <div class="col-md-6">
                  <field-input
                    id="form-production_place"
                    name="production_place"
                    :label="config.production_place.label"
                    :placeholder="config.production_place.placeholder"
                    :field="$v.production_place"
                    :messages="{
                      required: config.production_place.requiredErrorMessage,
                      serverError: getServerErrorMessage('production_place')
                    }"
                    :show-error="$v.$dirty"
                    :maxlength="400"
                  />
                </div>
                <div class="col-12">
                  <field-textarea
                    id="form-description"
                    name="description"
                    :label="config.description.label"
                    :placeholder="config.description.placeholder"
                    :field="$v.description"
                    :messages="{
                      required: config.description.requiredErrorMessage,
                      serverError: getServerErrorMessage('description')
                    }"
                    :show-error="$v.$dirty"
                    :maxlength="150"
                  >
                    <template #availableChars="{ chars }">
                      <template v-if="chars === 150">
                        {{ replaceValue(config.maxChars, chars) }}
                      </template>
                      <template v-else>
                        {{ replaceValue(config.availableChars, chars) }}
                      </template>
                    </template>
                  </field-textarea>
                </div>
                <div class="col-12">
                  <field-upload
                    id="form-attachment"
                    name="file"
                    :label="config.file.label"
                    :cta="config.file.cta"
                    :placeholder="config.file.placeholder"
                    accept="application/pdf"
                    :field="$v.file"
                    :messages="{
                      maxsize: config.file.maxsize,
                      filetype: config.file.filetype,
                      serverError: getServerErrorMessage('file')
                    }"
                    :show-error="$v.$dirty"
                  />
                </div>
                <div class="col-12">
                  <field-textarea
                    id="form-troubleshooting"
                    name="troubleshooting"
                    :label="config.troubleshooting.label"
                    :placeholder="config.troubleshooting.placeholder"
                    :field="$v.troubleshooting"
                    :messages="{
                      required: config.troubleshooting.requiredErrorMessage,
                      serverError: getServerErrorMessage('troubleshooting')
                    }"
                    :show-error="$v.$dirty"
                    :maxlength="150"
                  >
                    <template #availableChars="{ chars }">
                      <template v-if="chars === 150">
                        {{ replaceValue(config.maxChars, chars) }}
                      </template>
                      <template v-else>
                        {{ replaceValue(config.availableChars, chars) }}
                      </template>
                    </template>
                  </field-textarea>
                </div>
                <div class="col-12">
                  <field-textarea
                    id="form-market_segment"
                    name="market_segment"
                    :label="config.market_segment.label"
                    :placeholder="config.market_segment.placeholder"
                    :field="$v.market_segment"
                    :messages="{
                      required: config.market_segment.requiredErrorMessage,
                      serverError: getServerErrorMessage('market_segment')
                    }"
                    :show-error="$v.$dirty"
                    :maxlength="150"
                  >
                    <template #availableChars="{ chars }">
                      <template v-if="chars === 150">
                        {{ replaceValue(config.maxChars, chars) }}
                      </template>
                      <template v-else>
                        {{ replaceValue(config.availableChars, chars) }}
                      </template>
                    </template>
                  </field-textarea>
                </div>
                <div class="col-12">
                  <field-textarea
                    id="form-growth"
                    name="growth"
                    :label="config.growth.label"
                    :placeholder="config.growth.placeholder"
                    :field="$v.growth"
                    :messages="{
                      required: config.growth.requiredErrorMessage,
                      serverError: getServerErrorMessage('growth')
                    }"
                    :show-error="$v.$dirty"
                    :maxlength="150"
                  >
                    <template #availableChars="{ chars }">
                      <template v-if="chars === 150">
                        {{ replaceValue(config.maxChars, chars) }}
                      </template>
                      <template v-else>
                        {{ replaceValue(config.availableChars, chars) }}
                      </template>
                    </template>
                  </field-textarea>
                </div>
                <div class="col-12">
                  <field-textarea
                    id="form-strengths"
                    name="strengths"
                    :label="config.strengths.label"
                    :placeholder="config.strengths.placeholder"
                    :field="$v.strengths"
                    :messages="{
                      required: config.strengths.requiredErrorMessage,
                      serverError: getServerErrorMessage('strengths')
                    }"
                    :show-error="$v.$dirty"
                    :maxlength="150"
                  >
                    <template #availableChars="{ chars }">
                      <template v-if="chars === 150">
                        {{ replaceValue(config.maxChars, chars) }}
                      </template>
                      <template v-else>
                        {{ replaceValue(config.availableChars, chars) }}
                      </template>
                    </template>
                  </field-textarea>
                </div>
                <div class="col-12">
                  <field-textarea
                    id="form-price"
                    name="price"
                    :label="config.price.label"
                    :placeholder="config.price.placeholder"
                    :field="$v.price"
                    :messages="{
                      required: config.price.requiredErrorMessage,
                      serverError: getServerErrorMessage('price')
                    }"
                    :show-error="$v.$dirty"
                    :maxlength="150"
                  >
                    <template #availableChars="{ chars }">
                      <template v-if="chars === 150">
                        {{ replaceValue(config.maxChars, chars) }}
                      </template>
                      <template v-else>
                        {{ replaceValue(config.availableChars, chars) }}
                      </template>
                    </template>
                  </field-textarea>
                </div>
                <div class="col-12">
                  <field-checkbox
                    ref="$privacyAccept"
                    id="form-privacy-accept"
                    name="privacy_accept"
                    :field="$v.privacy_accept"
                    :messages="{
                      checked: privacy_accept_enabled
                        ? config.privacy_accept.requiredErrorMessage
                        : config.privacy_accept.disabledErrorMessage,
                      serverError: getServerErrorMessage('privacy_accept')
                    }"
                    :show-error="$v.privacy_accept.$dirty"
                    :disabled="!privacy_accept_enabled"
                    @click-on-disabled="$v.privacy_accept.$touch()"
                  >
                    <span v-html="stripTags(config.privacy_accept.label)" />
                  </field-checkbox>
                </div>
                <div class="col-12">
                  <field-checkbox
                    ref="$privacyField"
                    id="form-privacy-field"
                    name="privacy_field"
                    :field="$v.privacy_field"
                    :messages="{
                      checked: privacy_field_enabled
                        ? config.privacy_field.requiredErrorMessage
                        : config.privacy_field.disabledErrorMessage,
                      serverError: getServerErrorMessage('privacy_field')
                    }"
                    :show-error="$v.privacy_field.$dirty"
                    :disabled="!privacy_field_enabled"
                    @click-on-disabled="$v.privacy_field.$touch()"
                  >
                    <span v-html="stripTags(config.privacy_field.label)" />
                  </field-checkbox>
                </div>
              </div>
              <div class="submit-wrapper">
                <field-error :show="$v.$anyError && $v.$dirty && !genericError">
                  {{ config.fieldsError }}
                </field-error>
                <field-error :show="genericError && $v.$dirty">
                  {{ config.sendError }}
                </field-error>
                <btn tag="button" type="submit" class="blue-lochmara">
                  {{ config.send }}
                </btn>
              </div>
            </div>
          </div>
        </container>
      </form>
    </transition>
  </mounting-portal>
</template>

<script>
import { validationMixin } from "vuelidate";
import axios from "axios";
import Btn from "@components/atoms/btn";
import FieldInput from "@components/form/field-input";
import FieldTextarea from "@components/form/field-textarea";
import { email, required } from "vuelidate/lib/validators";
import {
  checked,
  filetype,
  maxsize,
  phone,
  serverError
} from "@helpers/validators";
import Container from "@components/layout/container";
import Icon from "@components/atoms/icon";
import FieldError from "@components/form/field-error";
import FieldCheckbox from "@components/form/field-checkbox";
import FieldSelect from "@components/form/field-select";
import FieldLabel from "@components/form/field-label";
import FieldUpload from "@components/form/field-upload";

export default {
  name: "BluFutureForm",
  components: {
    FieldUpload,
    FieldLabel,
    FieldSelect,
    FieldCheckbox,
    FieldError,
    Icon,
    Container,
    FieldTextarea,
    FieldInput,
    Btn
  },
  mixins: [validationMixin],
  props: {
    dataModalId: { type: String, required: true },
    dataApiUrl: { type: String, required: true },
    dataConfig: { type: String, required: true },
    dataLanguage: { type: String, required: true }
  },
  data() {
    return {
      cmsMenuOffset: 0,
      isOpen: false,
      loading: false,
      confirm: false,
      genericError: false,
      serverErrors: false,
      name: "",
      email: "",
      phone_prefix: "",
      phone: "",
      founder: "",
      employees: "",
      production_place: "",
      headquarter: "",
      description: "",
      file: null,
      troubleshooting: "",
      market_segment: "",
      growth: "",
      strengths: "",
      price: "",
      privacy_accept: false,
      privacy_field: false,
      privacy_accept_enabled: false,
      privacy_field_enabled: false,
      phonePrefixes: []
    };
  },
  validations() {
    return {
      name: { required, serverError: serverError(this.getServerError("name")) },
      email: {
        required,
        email,
        serverError: serverError(this.getServerError("email"))
      },
      phone_prefix: {
        required,
        serverError: serverError(this.getServerError("phone_prefix"))
      },
      phone: {
        required,
        phone,
        serverError: serverError(this.getServerError("phone"))
      },
      founder: {
        required,
        serverError: serverError(this.getServerError("founder"))
      },
      employees: {
        required,
        serverError: serverError(this.getServerError("employees"))
      },
      production_place: {
        required,
        serverError: serverError(this.getServerError("production_place"))
      },
      headquarter: {
        required,
        serverError: serverError(this.getServerError("headquarter"))
      },
      description: {
        required,
        serverError: serverError(this.getServerError("description"))
      },
      file: {
        maxsize: maxsize(10 * 1024), // 10 Mb
        filetype: filetype("application/pdf"),
        serverError: serverError(this.getServerError("file"))
      },
      troubleshooting: {
        required,
        serverError: serverError(this.getServerError("troubleshooting"))
      },
      market_segment: {
        required,
        serverError: serverError(this.getServerError("market_segment"))
      },
      growth: {
        required,
        serverError: serverError(this.getServerError("growth"))
      },
      strengths: {
        required,
        serverError: serverError(this.getServerError("strengths"))
      },
      price: {
        required,
        serverError: serverError(this.getServerError("price"))
      },
      privacy_accept: {
        checked: checked(),
        serverError: serverError(this.getServerError("privacy_accept"))
      },
      privacy_field: {
        checked: checked(),
        serverError: serverError(this.getServerError("privacy_field"))
      }
    };
  },
  computed: {
    config() {
      return JSON.parse(this.dataConfig);
    },
    flatParams() {
      const data = {};
      Object.keys(this.$v.$params).forEach(field => {
        if (field !== "phone_prefix") {
          if (typeof this.$v[field].$model === "boolean") {
            data[field] = this.$v[field].$model ? 1 : 0;
          } else {
            data[field] = this.$v[field].$model;
            if (field === "phone") {
              data[field] = `${this.phone_prefix}${data[field]}`;
            }
          }
        }
      });
      return data;
    }
  },
  mounted() {
    const toolbar = document.querySelector(".cms-toolbar");
    if (!!toolbar) {
      this.cmsMenuOffset = toolbar.clientHeight;
    }
    const links = document.querySelectorAll(`[href="#${this.dataModalId}"]`);
    [...links].forEach(link => {
      link.addEventListener("click", e => {
        e.preventDefault();
        e.stopPropagation();
        this.openModal();
        return false;
      });
    });
    if (this.dataLanguage === "it") {
      import("@data/phone_prefixes_it").then(this.setPhonePrefixes);
    } else {
      import("@data/phone_prefixes_en").then(this.setPhonePrefixes);
    }
  },
  methods: {
    getFormData() {
      const formData = new FormData();
      Object.keys(this.flatParams).forEach(field => {
        if (
          field !== "file" ||
          (field === "file" && this.flatParams[field] !== null)
        ) {
          formData.append(field, this.flatParams[field]);
        }
      });
      return formData;
    },
    setPhonePrefixes(data) {
      this.phonePrefixes = data.default.map(prefix => {
        const p = Object.values(prefix)[0];
        return {
          value: `+${p}`,
          label: `+${p}`
        };
      });
      this.phone_prefix = this.phonePrefixes[0].value;
    },
    stripTags(value) {
      return value.replace(/(<p[^>]+?>|<p>|<\/p>)/gim, "");
    },
    getServerError(field) {
      if (
        this.serverErrors &&
        this.serverErrors[field] &&
        this.serverErrors[field].length
      ) {
        return this.serverErrors[field];
      }
      return false;
    },
    getServerErrorMessage(field) {
      const serverError = this.getServerError(field);
      if (serverError) {
        return serverError[0];
      }
      return "";
    },
    keyDownHandler(e) {
      if (e.key === "Escape") {
        this.closeModal();
      }
    },
    openModal() {
      this.isOpen = true;
      document.body.style.overflowY = "hidden";
      document.addEventListener("keydown", this.keyDownHandler);
    },
    onAfterEnter() {
      this.$refs.$privacyAccept.$el.querySelectorAll("a").forEach(link => {
        link.setAttribute("target", "_blank");
        link.addEventListener("click", () => {
          this.privacy_accept_enabled = true;
        });
      });
      this.$refs.$privacyField.$el.querySelectorAll("a").forEach(link => {
        link.setAttribute("target", "_blank");
        link.addEventListener("click", () => {
          this.privacy_field_enabled = true;
        });
      });
    },
    closeModal() {
      if (this.loading) {
        return false;
      }
      this.isOpen = false;
      this.confirm = false;
      document.body.style.overflowY = "auto";
      document.removeEventListener("keydown", this.keyDownHandler);
    },
    replaceValue(text, value) {
      return text.replace("[value]", value);
    },
    async resetForm() {
      Object.keys(this.$v.$params).forEach(field => {
        if (typeof this.$v[field].$model === "boolean") {
          this.$v[field].$model = false;
        } else {
          this.$v[field].$model = "";
        }
      });
      await this.$nextTick();
      this.$v.$reset();
    },
    async onSubmit() {
      this.genericError = false;
      this.serverErrors = false;
      this.$v.$touch();
      if (!this.$v.$invalid) {
        if (this.loading) {
          return false;
        }
        this.loading = true;
        try {
          await axios.post(this.dataApiUrl, this.getFormData(), {
            headers: {
              "Content-Type": "multipart/form-data"
            }
          });
          await this.resetForm();
          this.loading = false;
          this.confirm = true;
        } catch (e) {
          if (
            e &&
            e.response &&
            e.response.status &&
            e.response.status === 400 &&
            e.response.data
          ) {
            this.serverErrors = e.response.data;
          } else {
            this.genericError = true;
          }
          this.loading = false;
        }
      }
    }
  }
};
</script>

<style lang="scss" scoped>
@import "~$scss/variables";
@import "~$scss/utility";

.BluFutureForm {
  --gutter-top: 28px;
  background-color: $color-champagne-light;
  position: fixed;
  left: 0;
  top: var(--offset-top, 0px);
  right: 0;
  bottom: 0;
  overflow: hidden;
  overflow-y: auto;
  @include ScrollTouchFix();

  @media (min-width: $bootstrap-md) {
    --gutter-top: 37px;
  }
}

.loading {
  position: fixed;
  left: 0;
  top: var(--offset-top, 0px);
  right: 0;
  bottom: 0;
  overflow: hidden;
  z-index: 101;
  background-color: rgba($color-champagne-light, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 64px;
  color: $color-blue;

  svg {
    display: block;
    width: 1em;
    height: 1em;
  }
}

.close {
  position: absolute;
  right: 24px;
  top: 24px;
  z-index: 2;
  padding: 1em;
  font-size: 18px;
  line-height: 1;
  margin: -1em -1em 0 0;
  color: $color-dark;
  transition: color 0.3s ease;
  outline: none;

  @media (min-width: $bootstrap-md) {
    right: 80px;
    top: 40px;
  }

  &:hover {
    color: $color-blue-lochmara;
  }

  .icon {
    display: block;
    width: 1em;
    height: 1em;
    text-align: center;
  }
}

.fields-container {
  position: relative;
  z-index: 1;
  padding: 0;
}

.main-row {
  padding-top: 80px;
  padding-bottom: 40px;
  min-height: 100vh;

  @media (min-width: $bootstrap-md) {
    padding-top: 100px;
    padding-bottom: 88px;
  }
}

.intro-col {
  text-align: center;
}

.fields-col {
  padding-top: 48px;

  @media (min-width: $bootstrap-md) {
    padding-top: 78px;
  }
}

.fields-row {
  margin-top: calc(var(--gutter-top, 0px) * -1);

  > [class^="col"] {
    padding-top: var(--gutter-top, 0px);
  }
}

.submit-wrapper {
  text-align: center;
  padding-top: 50px;
  --error-message-gutter-top: 0px;
  --error-message-gutter-bottom: 16px;

  @media (min-width: $bootstrap-md) {
    padding-top: 80px;
  }

  .Btn {
    min-width: 120px;
  }
}

.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.6s ease-in-out, transform 0.6s ease-in-out;
}

.modal-enter,
.modal-leave-to {
  opacity: 0;
  transform: scale(0.75);
}

.overlay-enter-active,
.overlay-leave-active {
  transition: opacity 0.6s ease-in-out;
}

.overlay-enter,
.overlay-leave-to {
  opacity: 0;
}
</style>
